import type { ComponentSpacing, SignageComponent } from '@/config/types';
import { useStableIds } from '@/hooks/useStableIds';
import { componentSpacingVars } from '@/widgets/spacing';
import { WidgetGrid } from '@/widgets/WidgetGrid';

/**
 * The non-slider rendering path: every component in config order, in one 12-column grid.
 *
 * DOM structure is the legacy `v-else` branch of `moduleLoader.vue`, class for class:
 *
 * ```
 * div.flex.px-0.flex-row.w-full.justify-center.min-w-500
 *   div.main-container                    (width: 100%; padding-inline: 0.5rem)
 *     div.widget-grid.w-full.h-full       (was .flex.flex-col — see WidgetGrid)
 *       div.widget-container  × n         (padding from layout.components, span from config)
 *         <WidgetSlot />
 * ```
 *
 * The inner column was `flex flex-col` until the responsive-layout work made it a grid.
 * Nothing about a stack of full-width widgets changes — a grid row and a flex line both size
 * to their content and both stretch their child to the full width — and every one of the seven
 * deployed configs is exactly that, because none of them carries a `span`. What the grid adds
 * is that a config *can* now carry one.
 *
 * It is kept in its own file so SYM-492 adds `SliderLayout.tsx` beside it and switches on it
 * in `ModuleLoader`, rather than restructuring the loader itself.
 *
 * `main-container`'s `padding-inline: 0.5rem` is a hardcoded legacy constant — the only
 * spacing here that does *not* come from config — expressed as `px-2` (0.25rem × 2). The
 * legacy class name is kept as a marker so a DOM diff against the Vue app stays readable.
 */

type FlatLayoutProps = {
  /** Rendered in array order; the index is the stable-id key. */
  components: SignageComponent[];
  spacing: ComponentSpacing;
};

export function FlatLayout({ components, spacing }: FlatLayoutProps) {
  const getId = useStableIds();

  return (
    <div className="flex px-0 flex-row w-full justify-center min-w-500">
      <div className="main-container w-full px-2" style={componentSpacingVars(spacing)}>
        <WidgetGrid
          items={components.map((component, index) => ({
            component,
            componentId: getId(index),
            span: component.span,
          }))}
        />
      </div>
    </div>
  );
}
